import { Box, Button, Flex, Loading, Text, createColumnHelper } from 'ui'
import { useMemo } from 'react'
import { usePulseContext } from '../../context/PulseProvider'
import { usePayRateMarketByLocation } from 'queries/pulse/usePayRateMarketByLocation'
import { PayRateMarketByLocation as PayRateMarketByLocationType } from 'api/pulse/payRateMarketByLocation'
import { formatCurrency, formatPercentage } from 'lib/string'
import { InfiniteTable } from '../InfiniteTable'
import { usePayRateMarketByLocationDownloadCsv } from 'queries/pulse/usePayRateMarketByLocationDownloadCsv'

const columnHelper = createColumnHelper<PayRateMarketByLocationType[0]>()

const columns = [
  columnHelper.accessor('company_name', {
    header: 'Location Name',
  }),
  columnHelper.accessor('position_type', {
    header: 'Position Type',
    cell: (info) => <Text>{info.getValue()}</Text>,
  }),
  columnHelper.accessor('avg_pay_rate_with_company_bonus', {
    header: 'Location Pay Rate',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatCurrency(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('avg_market_pay_rate', {
    header: 'Market Pay Rate',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatCurrency(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('pay_rate_difference_with_market', {
    header: 'Pay Rate Difference',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatCurrency(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
  columnHelper.accessor('fill_rate', {
    header: 'Fill Rate',
    cell: (info) => (
      <Text>
        {typeof info.getValue() === 'number'
          ? formatPercentage(info.getValue() as number)
          : 'N/A'}
      </Text>
    ),
  }),
]

export function PayRateMarketByLocation() {
  const {
    state: { filters },
  } = usePulseContext()

  const filtersWithPagination = useMemo(() => {
    return {
      ...filters,
      page: 0,
      pageSize: 50,
    }
  }, [filters])

  const { data, isLoading, isFetching, fetchNextPage, hasNextPage } =
    usePayRateMarketByLocation(filtersWithPagination)

  const { mutateAsync: downloadCsv, isPending: isDownloadingCsv } =
    usePayRateMarketByLocationDownloadCsv(filters)

  const flatData = useMemo(() => {
    return data?.pages.flatMap((page) => page) ?? []
  }, [data])

  return (
    <Box>
      <Flex
        flexDirection="row"
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        mb={3}
      >
        <Box>
          <Text fontSize={3} fontWeight="500">
            Pay Rate Competitiveness - by Location
          </Text>
          <Text fontSize={2} color="lightText">
            Pay Rate Competitiveness inter-location evaluation
          </Text>
        </Box>
        <Button
          variant="secondary"
          onClick={() => downloadCsv()}
          loading={isDownloadingCsv}
        >
          CSV
        </Button>
      </Flex>
      {isLoading ? (
        <Box p={3}>
          <Loading />
        </Box>
      ) : (
        <InfiniteTable
          data={flatData}
          columns={columns}
          isFetching={isFetching}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage}
          height="400px"
        />
      )}
    </Box>
  )
}
